import React, { useEffect, useState } from "react";
import { supabase } from "../../../utils/supabase";
import NewClientModal from "../../client-analytics/components/NewClientModal";

/* ──────────────────────────────────────────────────────────────
   Helpers sucursal
─────────────────────────────────────────────────────────────── */
function formatBranchLabel(branch_no, branch_name) {
  if (branch_no == null) return "";
  const bn = String(branch_no).trim();
  const bname = (branch_name || "").trim();
  return bname ? `Sucursal ${bn} - ${bname}` : `Sucursal ${bn}`;
}

function formatClientShort(c) {
  if (!c) return "";
  const cn = c.client_number ?? "—";
  const bNo = c.branch_no != null ? ` ${c.branch_no}` : "";
  const bLabel = formatBranchLabel(c.branch_no, c.branch_name);
  const cuitLabel = c.cuit ? ` · CUIT ${c.cuit}` : (c.tax_id ? ` · ${c.tax_id}` : "");
  const branchExtra = bLabel ? ` · ${bLabel}` : "";
  return `${cn}${bNo} — ${c.name || "—"}${branchExtra}${cuitLabel}`;
}

/* ──────────────────────────────────────────────────────────────
   BÚSQUEDA DE CLIENTES: Nº cliente, CUIT o nombre (smart search)
   (igual que en NewOfferModal) + SUCURSALES
─────────────────────────────────────────────────────────────── */
async function searchClientsSmart(q, limit = 20) {
  // ✅ agregamos branch_no y branch_name
  const baseSelect = "id,name,client_number,cuit,tax_id,branch_no,branch_name";

  const s = (q ?? "").trim();
  if (!s) return [];

  const digitsOnly = s.replace(/\D/g, "");
  const isAllDigits = /^\d+$/.test(s);
  const isLikelyCuit = /^\d{8,11}$/.test(digitsOnly);

  // Nº de cliente exacto (cuando NO parece CUIT)
  if (isAllDigits && !isLikelyCuit) {
    const { data, error } = await supabase
      .from("clients")
      .select(baseSelect)
      .eq("client_number", Number(s))
      .order("branch_no", { ascending: true, nullsFirst: true })
      .order("name", { ascending: true })
      .limit(limit);
    if (error) throw error;
    return data ?? [];
  }

  // CUIT exacto / like
  if (isLikelyCuit) {
    const exact = await supabase
      .from("clients")
      .select(baseSelect)
      .eq("cuit", digitsOnly)
      .order("branch_no", { ascending: true, nullsFirst: true })
      .limit(limit);
    if (exact.error) throw exact.error;
    if ((exact.data ?? []).length) return exact.data;

    const like = await supabase
      .from("clients")
      .select(baseSelect)
      .ilike("cuit", `%${digitsOnly}%`)
      .order("branch_no", { ascending: true, nullsFirst: true })
      .order("name", { ascending: true })
      .limit(limit);
    if (like.error) throw like.error;
    return like.data ?? [];
  }

  // Nombre
  const { data, error } = await supabase
    .from("clients")
    .select(baseSelect)
    .ilike("name", `%${s}%`)
    .order("name", { ascending: true })
    .order("branch_no", { ascending: true, nullsFirst: true })
    .limit(limit);
  if (error) throw error;
  return data ?? [];
}

export default function CreateIntakeModal({ open, onClose, onCreated }) {
  const [clientQuery, setClientQuery] = useState("");
  const [clientResults, setClientResults] = useState([]);
  const [clientLoading, setClientLoading] = useState(false);

  const [selectedClient, setSelectedClient] = useState(null);
  const [clientInfo, setClientInfo] = useState(null);

  const [saving, setSaving] = useState(false);
  const [newClientOpen, setNewClientOpen] = useState(false);

  useEffect(() => {
    if (!open) {
      setClientQuery("");
      setClientResults([]);
      setClientLoading(false);
      setSelectedClient(null);
      setClientInfo(null);
      setSaving(false);
      setNewClientOpen(false);
      return;
    }
  }, [open]);

  // Autocomplete de clientes (smart)
  useEffect(() => {
    const q = (clientQuery || "").trim();
    if (!q || selectedClient) {
      setClientResults([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        setClientLoading(true);
        const rows = await searchClientsSmart(q, 20);
        setClientResults(rows || []);
      } catch (e) {
        console.error("client search", e);
        setClientResults([]);
      } finally {
        setClientLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [clientQuery, selectedClient]);

  // Details del cliente seleccionado
  useEffect(() => {
    if (!selectedClient?.id) {
      setClientInfo(null);
      return;
    }
    (async () => {
      try {
        const { data, error } = await supabase
          .from("clients")
          // ✅ traemos sucursal también
          .select("id,name,cuit,tax_id,client_number,branch_no,branch_name,contact,email,phone,address")
          .eq("id", selectedClient.id)
          .single();
        if (error) throw error;
        setClientInfo(data || null);
      } catch (e) {
        console.error("load client details", e);
        setClientInfo(null);
      }
    })();
  }, [selectedClient?.id]);

  async function handleCreate() {
    try {
      setSaving(true);
      if (!selectedClient?.id) {
        alert("Seleccioná un cliente o creá uno nuevo.");
        return;
      }
      const { data, error } = await supabase.rpc("create_intake", {
        p_client_id: selectedClient.id,
      });
      if (error) throw error;

      const intakeId = data; // uuid
      onCreated?.(intakeId, selectedClient);
    } catch (e) {
      alert(e?.message || "No se pudo crear el ingreso provisorio");
    } finally {
      setSaving(false);
    }
  }

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4"
      onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}
    >
      <div
        className="w-full max-w-2xl rounded-2xl bg-[#0F1115] text-white border border-white/10 shadow-2xl p-5"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Nuevo ingreso (OS) — Elegir/crear cliente</h3>
          <button className="px-3 py-1.5 rounded-md border border-white/10 hover:bg-white/10" onClick={onClose}>
            Cerrar
          </button>
        </div>

        {/* Buscador / selector de cliente */}
        <label className="text-sm opacity-80">Cliente</label>
        <div className="relative">
          <input
            className="w-full mt-1 mb-2 px-3 py-2 rounded-md bg-white/10 border border-white/10"
            placeholder="Buscar por Nº cliente, nombre o CUIT…"
            value={selectedClient ? formatClientShort(selectedClient) : clientQuery}
            onChange={(e) => {
              setSelectedClient(null);
              setClientInfo(null);
              setClientQuery(e.target.value);
            }}
          />
          {/* Resultados */}
          {!selectedClient && clientQuery.trim() && (clientLoading || clientResults.length > 0) && (
            <div className="absolute left-0 right-0 z-[90] mt-1 w-full max-h-72 overflow-auto rounded-xl border border-white/10 shadow-2xl bg-[#0F1115]">
              {clientLoading && (
                <div className="px-3 py-2 text-sm text-white/70">Buscando…</div>
              )}

              {!clientLoading &&
                clientResults.map((c) => {
                  const cn = c.client_number ?? "—";
                  const bNo = c.branch_no != null ? ` ${c.branch_no}` : "";
                  const branchLabel = formatBranchLabel(c.branch_no, c.branch_name);

                  return (
                    <button
                      key={c.id}
                      type="button"
                      className="w-full px-3 py-2 text-left hover:bg-white/10 focus:bg-white/10 focus:outline-none"
                      onClick={() => {
                        setSelectedClient(c);
                        setClientQuery("");
                        setClientResults([]);
                      }}
                    >
                      <div className="text-sm font-medium">
                        {cn}
                        {bNo} — {c.name}
                        {branchLabel ? (
                          <span className="ml-2 text-xs opacity-70">({branchLabel})</span>
                        ) : null}
                      </div>
                      <div className="text-xs opacity-70">
                        {c.cuit ? `CUIT ${c.cuit}` : c.tax_id || "—"}
                      </div>
                    </button>
                  );
                })}

              {!clientLoading && clientResults.length === 0 && (
                <div className="px-3 py-2 text-sm opacity-70">Sin resultados</div>
              )}
            </div>
          )}
        </div>

        {/* Botón Nuevo cliente + ficha compacta */}
        <div className="mt-2 flex flex-col md:flex-row md:items-start md:gap-3">
          <button
            type="button"
            className="inline-flex items-center rounded-lg border border-white/10 px-3 py-1.5 text-sm hover:bg-white/10"
            onClick={() => setNewClientOpen(true)}
          >
            + Nuevo cliente
          </button>

          {clientInfo && (
            <div className="mt-3 md:mt-0 grow rounded-lg border border-white/10 bg-white/5 px-4 py-3">
              <div className="flex items-start justify-between">
                <div className="text-base font-semibold">{clientInfo.name}</div>
                <button
                  className="text-xs underline opacity-80 hover:opacity-100 ml-2"
                  onClick={() => {
                    setSelectedClient(null);
                    setClientInfo(null);
                  }}
                >
                  Cambiar
                </button>
              </div>

              <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
                <div>
                  <span className="opacity-70">CUIT:</span>
                  <span className="ml-2 font-medium">{clientInfo.cuit || clientInfo.tax_id || "—"}</span>
                </div>
                <div>
                  <span className="opacity-70">Nº Cliente:</span>
                  <span className="ml-2 font-medium">
                    {clientInfo.client_number || "—"}
                    {clientInfo.branch_no != null ? ` ${clientInfo.branch_no}` : ""}
                  </span>
                </div>

                {/* ✅ Sucursal */}
                <div className="sm:col-span-2">
                  <span className="opacity-70">Sucursal/Planta:</span>
                  <span className="ml-2">
                    {formatBranchLabel(clientInfo.branch_no, clientInfo.branch_name) || "—"}
                  </span>
                </div>

                <div>
                  <span className="opacity-70">Contacto:</span>
                  <span className="ml-2">{clientInfo.contact || "—"}</span>
                </div>
                <div>
                  <span className="opacity-70">Teléfono:</span>
                  <span className="ml-2">{clientInfo.phone || "—"}</span>
                </div>
                <div className="sm:col-span-2">
                  <span className="opacity-70">Email:</span>
                  <span className="ml-2 truncate">{clientInfo.email || "—"}</span>
                </div>
                <div className="sm:col-span-2">
                  <span className="opacity-70">Dirección:</span>
                  <span className="ml-2">{clientInfo.address || "—"}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 mt-5">
          <button className="px-3 py-2 rounded-md border border-white/10" onClick={onClose}>
            Cancelar
          </button>
          <button
            className="px-3 py-2 rounded-md bg-emerald-600 hover:bg-emerald-500"
            disabled={!selectedClient?.id || saving}
            onClick={handleCreate}
          >
            {saving ? "Creando…" : "Crear ingreso"}
          </button>
        </div>

        {/* Modal: Nuevo Cliente */}
        <NewClientModal
          isOpen={newClientOpen}
          onClose={() => setNewClientOpen(false)}
          onCreated={(row) => {
            setSelectedClient(row);
            setClientQuery("");
            setClientResults([]);
            setNewClientOpen(false);
          }}
        />
      </div>
    </div>
  );
}
